import { useState } from 'react';
import { Link } from 'react-router';
import { useAiCost } from '../api/hooks';
import type { AiCost, AiCostDays, AiCostPlayer } from '../api/types';
import { PageHeader } from '../app/Shell';
import { AI_PURPOSE } from '../app/labels';
import { Badge, Button, Card, DataTable, Icon, QueryView, ScoreBar, Segmented, Stat, StatGrid, fmtInt, fmtPct, fmtUsd } from '../ui';

const RANGES: ReadonlyArray<{ value: `${AiCostDays}`; label: string }> = [
  { value: '7', label: '7 日' },
  { value: '30', label: '30 日' },
  { value: '90', label: '90 日' },
];

const usd = (n: number) => `US${fmtUsd(n)}`;
const purposeLabel = (p: string) => AI_PURPOSE[p] ?? p;
const playerName = (p: AiCostPlayer) => p.characterName ?? p.displayName ?? p.email ?? p.publicId ?? '（未建立角色）';

/** AI spend per player against the planning target (about US$2 per player per 30 days; a yardstick, never a limit). */
export function AiCostPage() {
  const [days, setDays] = useState<AiCostDays>(30);
  const q = useAiCost(days);
  return (
    <>
      <PageHeader
        title="AI 成本"
        description={q.data ? `${q.data.from} 至 ${q.data.to}（UTC，包括今日）· 目標只作規劃參考，不會限制任何玩家` : '目標只作規劃參考，不會限制任何玩家'}
        actions={
          <>
            <Segmented label="時間範圍" value={`${days}`} options={RANGES} onChange={(v) => setDays(Number(v) as AiCostDays)} />
            <Button onClick={() => void q.refetch()} busy={q.isFetching}>
              <Icon name="refresh" size={16} /> 重新整理
            </Button>
          </>
        }
      />
      <QueryView query={q} loading="正在計算 AI 成本…">
        {(d) => <AiCostBody d={d} />}
      </QueryView>
    </>
  );
}

function AiCostBody({ d }: { d: AiCost }) {
  const p = d.players;
  const target = d.range === 30 ? `US$${d.target.usdPer30Days} 目標` : `US$${d.target.usdPer30Days} / 30 日目標（${d.range} 日折算 ${usd(d.target.usd)}）`;
  return (
    <>
      <StatGrid>
        <Stat label="總成本" value={usd(d.totalUsd)} hint={`${fmtInt(d.calls)} 次呼叫 · 其中系統 ${usd(d.systemUsd)}`} />
        <Stat label="有用 AI 的玩家" value={fmtInt(p.count)} hint={`玩家合共 ${usd(p.totalUsd)}`} />
        <Stat label="每位玩家平均" value={usd(p.averageUsd)} tone={p.averageUsd > d.target.usd ? 'warn' : undefined} hint={`對比 ${target}`} />
        <Stat label="中位數" value={usd(p.medianUsd)} hint="一半玩家花費低於此數" />
        <Stat label={`超過 US$${d.target.usdPer30Days} 目標的比例`} value={fmtPct(p.overTarget, p.count)} hint={`${fmtInt(p.overTarget)} / ${fmtInt(p.count)} 位玩家${d.range === 30 ? '' : ` · ${d.range} 日折算 ${usd(d.target.usd)}`}`} />
      </StatGrid>

      <Card title="按用途" sub={`過去 ${d.range} 日 · 系統 = 不屬於任何玩家的呼叫`} flush>
        <DataTable
          rows={d.byPurpose}
          rowKey={(r) => r.purpose}
          initialSort={{ key: 'usd', dir: 'desc' }}
          empty="這段時間沒有 AI 呼叫。"
          columns={[
            {
              key: 'purpose',
              header: '用途',
              render: (r) => (
                <span className="nowrap">
                  {purposeLabel(r.purpose)} <code className="faint small">{r.purpose}</code>
                </span>
              ),
              sort: (r) => r.purpose,
            },
            { key: 'usd', header: '成本', align: 'right', render: (r) => usd(r.usd), sort: (r) => r.usd },
            { key: 'share', header: '佔比', align: 'right', render: (r) => fmtPct(r.usd, d.totalUsd), sort: (r) => r.usd },
            { key: 'calls', header: '呼叫', align: 'right', render: (r) => fmtInt(r.calls), sort: (r) => r.calls },
            { key: 'images', header: '圖像', align: 'right', render: (r) => (r.images ? fmtInt(r.images) : <span className="faint">—</span>), sort: (r) => r.images },
            { key: 'system', header: '系統', align: 'right', render: (r) => (r.systemUsd ? usd(r.systemUsd) : <span className="faint">—</span>), sort: (r) => r.systemUsd },
          ]}
        />
      </Card>

      <Card title="花費最多的玩家" sub={`首 ${d.top.length} 位 · 長條以 ${usd(d.target.usd)} 目標為滿`} flush>
        <DataTable
          rows={d.top}
          rowKey={(r) => r.userId}
          initialSort={{ key: 'usd', dir: 'desc' }}
          pageSize={25}
          empty="這段時間沒有玩家用過 AI。"
          columns={[
            {
              key: 'name',
              header: '玩家',
              render: (r) => (
                <span className="stack" style={{ gap: 0 }}>
                  <Link to={`/users/${r.userId}`}>{playerName(r)}</Link>
                  <span className="faint small nowrap">
                    {r.email ?? '訪客'}
                    {r.publicId ? ` · ${r.publicId}` : ''}
                  </span>
                </span>
              ),
              sort: (r) => playerName(r),
            },
            { key: 'paying', header: '付費', render: (r) => (r.paying ? <Badge tone="ok">付費玩家</Badge> : <span className="faint">—</span>), sort: (r) => (r.paying ? 1 : 0) },
            { key: 'usd', header: '成本', align: 'right', render: (r) => <span className={r.totalUsd > d.target.usd ? 'neg' : undefined}>{usd(r.totalUsd)}</span>, sort: (r) => r.totalUsd },
            {
              key: 'target',
              header: '對比目標',
              width: 170,
              render: (r) => (
                <span style={{ display: 'grid', gridTemplateColumns: '1fr 3.5em', alignItems: 'center', gap: 8 }}>
                  <ScoreBar value={(r.totalUsd / d.target.usd) * 100} color={r.totalUsd > d.target.usd ? 'var(--danger)' : 'var(--chart-1)'} />
                  <span className="small num">{fmtPct(r.totalUsd, d.target.usd)}</span>
                </span>
              ),
              sort: (r) => r.totalUsd,
            },
            {
              key: 'purposes',
              header: '用途分佈',
              render: (r) => (
                <span className="small">
                  {r.byPurpose
                    .slice(0, 3)
                    .map((x) => `${purposeLabel(x.purpose)} ${usd(x.usd)}`)
                    .join(' · ')}
                  {r.byPurpose.length > 3 && <span className="faint"> · 另 {r.byPurpose.length - 3} 項</span>}
                </span>
              ),
            },
            { key: 'images', header: '繪圖', align: 'right', render: (r) => (r.images ? fmtInt(r.images) : <span className="faint">—</span>), sort: (r) => r.images },
            { key: 'calls', header: '呼叫', align: 'right', render: (r) => fmtInt(r.calls), sort: (r) => r.calls },
            { key: 'days', header: '首次 / 最後', render: (r) => <span className="nowrap small">{r.firstDay === r.lastDay ? r.firstDay.slice(5) : `${r.firstDay.slice(5)} – ${r.lastDay.slice(5)}`}</span>, sort: (r) => r.lastDay },
          ]}
        />
      </Card>
    </>
  );
}
